import { Link } from "react-router-dom";
import type { Video } from "@/mocks/videos";
import { formatCount, getCategoryName } from "@/utils/video";

type VideoCardProps = {
  video: Video;
  showCategory?: boolean;
  rank?: number;
};

export default function VideoCard({ video, showCategory = true, rank }: VideoCardProps) {
  return (
    <Link
      to={`/detail/${video.id}`}
      className="focus-ring group block rounded-lg"
      aria-label={`查看视频 ${video.title}`}
    >
      <div className="relative aspect-video w-full overflow-hidden rounded-lg border border-background-200/90 bg-background-200">
        <img
          src={video.cover}
          alt={`${video.title} 视频封面`}
          title={`${video.title} - ${getCategoryName(video.category)}高清视频封面`}
          className="h-full w-full object-cover object-top transition-transform duration-500 ease-out group-hover:scale-[1.05]"
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-primary-950/75 via-primary-950/10 to-transparent" />
        {showCategory ? (
          <span className="absolute left-2 top-2 rounded bg-accent-500 px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-primary-950">
            {getCategoryName(video.category)}
          </span>
        ) : null}
        {typeof rank === "number" ? (
          <span className="absolute right-2 top-2 flex h-6 min-w-6 items-center justify-center rounded bg-primary-950/75 px-1 font-heading text-[11px] font-semibold text-accent-300">
            {rank}
          </span>
        ) : null}
        <span className="absolute bottom-2 right-2 rounded bg-primary-950/80 px-1.5 py-0.5 font-label text-[11px] font-medium text-background-50">
          {video.duration}
        </span>
      </div>

      <div className="pt-3">
        <h3 className="line-clamp-1 text-sm font-semibold text-foreground-900 transition-colors group-hover:text-secondary-600 md:text-[15px]">
          {video.title}
        </h3>
        <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-foreground-500">
          {video.summary}
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-foreground-500">
          <span>{video.year}</span>
          <span className="text-background-300">/</span>
          <span>{video.region}</span>
          <span className="text-background-300">/</span>
          <span className="inline-flex items-center gap-1">
            <i className="ri-eye-line text-[11px]" aria-hidden="true" />
            {formatCount(video.views)}
          </span>
        </div>
      </div>
    </Link>
  );
}